import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, type BadgeProps, LoadingSpinner, Table, TextLink, Tooltip } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon, Trash2Icon } from 'lucide-react';
import type { Plan } from '../types/plan.types';

const statusBadgeVariant = (status: string): BadgeProps['variant'] =>
    status === 'active' ? 'success' : status === 'retired' ? 'error' : 'outline';

interface Props {
    data: Plan[];
    isLoading?: boolean;
    onDelete?: (id: string) => void;
}

export const PlanDataTable: React.FC<Props> = ({ data, isLoading = false, onDelete }) => {
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<Plan>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: (info) => {
                const status = info.getValue() as string;
                return <Badge variant={statusBadgeVariant(status)}>{status}</Badge>;
            },
        },
        {
            accessorKey: 'created_at',
            header: 'Created',
            cell: (info) => <span className="text-sm">{new Date(info.getValue() as string).toLocaleDateString()}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View plan">
                        <TextLink
                            onClick={() => navigate(`/smr-admin/plans/${info.row.original.id}`)}
                            color="primary"
                            ariaLabel="View plan"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                    <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                        <Tooltip content="Edit plan">
                            <TextLink
                                onClick={() => navigate(`/smr-admin/plans/${info.row.original.id}/edit`)}
                                color="secondary"
                                ariaLabel="Edit plan"
                                startIcon={<EditIcon size={18} />}
                            />
                        </Tooltip>
                    </PermissionGuard>
                    <PermissionGuard permission={{ resource: 'smr', action: 'plan.delete' }}>
                        {onDelete && (
                            <Tooltip content="Delete plan">
                                <TextLink
                                    onClick={() => onDelete(info.row.original.id)}
                                    color="error"
                                    ariaLabel="Delete plan"
                                    startIcon={<Trash2Icon size={18} />}
                                />
                            </Tooltip>
                        )}
                    </PermissionGuard>
                </div>
            ),
        },
    ], [navigate, onDelete]);

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading plans..." />;
    }

    return <Table<Plan> data={data} columns={columns} enableSorting={false} />;
};
